import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import Hero from '../components/Hero';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparisonProducts, setComparisonProducts] = useState([]);
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('https://janvaahan.com/backend/public/api/products');
      setProducts(response.data);
      // Get top 4 products for comparison (or all if less than 4)
      setComparisonProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setNewsletterStatus('Please enter your email address');
      return;
    }
    
    try {
      // You can implement newsletter subscription API here
      console.log('Newsletter subscription:', email);
      setNewsletterStatus('Thank you for subscribing!');
      setEmail('');
      setTimeout(() => setNewsletterStatus(''), 3000);
    } catch (error) {
      setNewsletterStatus('Something went wrong. Please try again.');
    }
  };

  // Format price for display
  const formatPrice = (price) => {
    if (!price) return 'Coming Soon';
    return price;
  };

  return (
    <div>
      <Hero />
      
      {/* Featured Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Electric Bike: Ride the Revolution</h2>
            <p className="text-xl text-gray-600">Discover our range of high-performance electric bikes</p>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No products available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Janvahan?</h2>
            <p className="text-xl text-gray-600">Experience the future of mobility with our cutting-edge technology</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 hover:shadow-xl transition-all duration-300 rounded-lg group">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-red-600 transition-colors duration-300">
                <span className="text-4xl group-hover:text-white transition-colors duration-300">⚡</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Zero Emissions</h3>
              <p className="text-gray-600">Eco-friendly electric mobility for a cleaner tomorrow. Reduce your carbon footprint with every ride.</p>
            </div>
            <div className="text-center p-6 hover:shadow-xl transition-all duration-300 rounded-lg group">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-red-600 transition-colors duration-300">
                <span className="text-4xl group-hover:text-white transition-colors duration-300">💰</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Low Running Cost</h3>
              <p className="text-gray-600">Save up to ₹20,000 annually on fuel and maintenance. Electric is the smart choice for your wallet.</p>
            </div>
            <div className="text-center p-6 hover:shadow-xl transition-all duration-300 rounded-lg group">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-red-600 transition-colors duration-300">
                <span className="text-4xl group-hover:text-white transition-colors duration-300">🔋</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Fast Charging</h3>
              <p className="text-gray-600">80% charge in just 2 hours with our rapid charger. Never wait long for your next ride.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-4xl font-bold mb-6">Advanced Technology</h2>
              <p className="text-xl mb-8 text-gray-300">Experience the perfect blend of innovation and performance with our state-of-the-art electric bikes.</p>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <span className="text-red-500 mr-3 text-xl">✓</span>
                  <span>AI-Powered Battery Management System</span>
                </li>
                <li className="flex items-center">
                  <span className="text-red-500 mr-3 text-xl">✓</span>
                  <span>Smart Connectivity & Mobile App Integration</span>
                </li>
                <li className="flex items-center">
                  <span className="text-red-500 mr-3 text-xl">✓</span>
                  <span>Regenerative Braking Technology</span>
                </li>
                <li className="flex items-center">
                  <span className="text-red-500 mr-3 text-xl">✓</span>
                  <span>GPS Tracking & Anti-Theft System</span>
                </li>
              </ul>
              <Link to="/products" className="inline-block mt-8 bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition transform hover:scale-105">
                Learn More
              </Link>
            </div>
            <div className="relative" data-aos="fade-left">
              <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-lg p-8 transform hover:scale-105 transition duration-500">
                <div className="bg-black/50 backdrop-blur-sm rounded-lg p-6">
                  <h3 className="text-2xl font-bold mb-4">Janvahan Smart App</h3>
                  <p className="text-gray-300 mb-4">Monitor your bike's performance, locate charging stations, and plan your routes with our intelligent mobile application.</p>
                  <div className="flex space-x-4">
                    <button className="bg-gray-800 px-4 py-2 rounded hover:bg-gray-700 transition">App Store</button>
                    <button className="bg-gray-800 px-4 py-2 rounded hover:bg-gray-700 transition">Google Play</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Models Comparison Section - DYNAMIC */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Compare Models</h2>
            <p className="text-xl text-gray-600">Find the perfect electric bike for your needs</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : comparisonProducts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No products available for comparison.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Model</th>
                    <th className="px-6 py-4 text-left">Range</th>
                    <th className="px-6 py-4 text-left">Top Speed</th>
                    <th className="px-6 py-4 text-left">Charging Time</th>
                    <th className="px-6 py-4 text-left">Price</th>
                    <th className="px-6 py-4 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonProducts.map((product, index) => (
                    <tr key={product.id} className={`border-b hover:bg-gray-50 transition ${index === comparisonProducts.length - 1 ? '' : 'border-b'}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {product.image_url && (
                            <img 
                              src={product.image_url.startsWith('http') ? product.image_url : `https://janvaahan.com${product.image_url}`}
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <div className="font-semibold">{product.name}</div>
                            <div className="text-xs text-gray-500">{product.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-lg mr-1">⚡</span>
                          <span>{product.range || 'N/A'} km</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-lg mr-1">🚀</span>
                          <span>{product.top_speed || 'N/A'} km/h</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-lg mr-1">🔋</span>
                          <span>{product.charging_time || '2-3'} hours</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-red-600 font-bold">
                        {formatPrice(product.price)}
                      </td>
                      <td className="px-6 py-4">
                        <Link 
                          to={`/product/${product.id}`}
                          className="inline-block bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="text-center mt-8">
            <Link 
              to="/products" 
              className="inline-block bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
            >
              View All Models →
            </Link>
            <p className="text-sm text-gray-500 mt-4">*Prices may vary by location and include applicable taxes</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">What Our Riders Say</h2>
            <p className="text-xl text-gray-600">Join thousands of satisfied Janvahan owners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
              <div className="text-red-500 text-4xl mb-4">"</div>
              <p className="text-gray-600 mb-6">Best decision I ever made! The JV1 has transformed my daily commute. Zero emissions and incredible savings on fuel.</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  RS
                </div>
                <div>
                  <h4 className="font-bold">Rahul Sharma</h4>
                  <p className="text-sm text-gray-500">Delhi, Owner since 2023</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
              <div className="text-red-500 text-4xl mb-4">"</div>
              <p className="text-gray-600 mb-6">Amazing performance and build quality. The BlazeX is perfect for long rides. Highly recommend Janvahan!</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  PP
                </div>
                <div>
                  <h4 className="font-bold">Priya Patel</h4>
                  <p className="text-sm text-gray-500">Mumbai, Owner since 2024</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition transform hover:-translate-y-1">
              <div className="text-red-500 text-4xl mb-4">"</div>
              <p className="text-gray-600 mb-6">Great service and support. The fast charging feature is a game-changer for city commutes. Love my JV400!</p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  AK
                </div>
                <div>
                  <h4 className="font-bold">Amit Kumar</h4>
                  <p className="text-sm text-gray-500">Bangalore, Owner since 2023</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Make the Switch?</h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">Join the electric revolution today and experience the future of mobility with Janvahan.</p>
          <div className="space-x-4">
            <Link to="/test-ride" className="inline-block bg-white text-red-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105">
              Book Test Ride
            </Link>
            <Link to="/products" className="inline-block border-2 border-white text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-white hover:text-red-600 transition transform hover:scale-105">
              View Models
            </Link>
          </div>
        </div>
      </section>

      {/* Charging Infrastructure Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-4xl font-bold mb-6">Charging Network</h2>
              <p className="text-xl text-gray-600 mb-8">Access to India's largest electric vehicle charging network. Never worry about running out of power.</p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition">
                  <span className="text-3xl font-bold text-red-600">500+</span>
                  <p className="text-gray-600">Charging Stations</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition">
                  <span className="text-3xl font-bold text-red-600">25+</span>
                  <p className="text-gray-600">Cities Covered</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition">
                  <span className="text-3xl font-bold text-red-600">24/7</span>
                  <p className="text-gray-600">Customer Support</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition">
                  <span className="text-3xl font-bold text-red-600">2 hrs</span>
                  <p className="text-gray-600">Fast Charging</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-red-500 to-red-700 p-8 rounded-lg text-white transform hover:scale-105 transition duration-500" data-aos="fade-left">
              <h3 className="text-2xl font-bold mb-4">Find a Charging Station</h3>
              <p className="mb-6">Locate the nearest charging point with our interactive map.</p>
              <input 
                type="text" 
                placeholder="Enter your city or pincode" 
                className="w-full px-4 py-2 rounded-lg text-black mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button className="w-full bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition">
                Search Stations
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-gray-600 mb-8">Subscribe to our newsletter for the latest updates, offers, and electric vehicle news.</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address" 
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
            <button 
              type="submit" 
              className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition transform hover:scale-105"
            >
              Subscribe
            </button>
          </form>
          {newsletterStatus && (
            <p className={`text-sm mt-4 ${newsletterStatus.includes('Thank you') ? 'text-green-600' : 'text-red-600'}`}>
              {newsletterStatus}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-4">No spam, unsubscribe anytime.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;